import {
  SceneConfig,
  SceneCapabilities,
  LoadingMode,
  SceneCapabilityStates,
} from '../types/sceneConfig';

// ─────────────────────────────────────────────────────────────────────────────
// inferLoadingMode
// Single source of truth for the segmented vs single-scene decision.
// ─────────────────────────────────────────────────────────────────────────────

export function inferLoadingMode(scene: SceneConfig): LoadingMode {
  if (scene.manifest) return 'segmented';
  if (scene.gaus) return 'single';
  return 'unsupported';
}

// ─────────────────────────────────────────────────────────────────────────────
// inferCapabilities
// Called after the scene loads (so hasPois is known).
// Returns a frozen capabilities object — components read this, never mutate it.
// ─────────────────────────────────────────────────────────────────────────────

export function inferCapabilities(
  scene: SceneConfig,
  hasPois: boolean
): SceneCapabilities {
  const signals = inferCapabilitySignals(scene);

  return Object.freeze({
    measurement: true as const,
    minimap:     signals.hasPathNetwork,
    pois:        hasPois,
    flythrough:  signals.supportsAerialFlythrough ||
                  signals.supportsOrbitFlythrough ||
                  signals.supportsPoseWalkthrough,
    imageView:   canUseSourceImages(scene),
    ...signals,
  });
}

function canUseSourceImages(scene: SceneConfig) {
  // Terrebonne's single-scene viewer is opened from GeoAutomation and uses
  // the Image View action to hand the current camera position back to the
  // embedding 2D map. It does not have the segmented source-image metadata
  // required by the regular SourceImages workflow.
  if (scene.sceneId.toLowerCase() === 'terrebonne-segment') return true;

  // Current SourceImages workflow needs georeferenced scene context plus
  // source-image metadata. imageBasePath alone is not enough.
  return !!(scene.imageBasePath && scene.imagePoses && scene.manifest);
}

function inferCapabilitySignals(scene: SceneConfig) {
  const hasGeoAlignment = !!scene.hasGeoAlignment;
  const hasImagePoses = !!scene.imagePoses;
  const hasPathNetwork = !!scene.pathNetworkUrl;
  const supportsAerialFlythrough = scene.sceneType === 'aerial';
  const supportsOrbitFlythrough =
    !!scene.gaus &&
    scene.sceneType !== 'indoor' &&
    scene.sceneType !== 'object';
  const supportsPoseWalkthrough =
    scene.sceneType === 'street' &&
    (hasImagePoses || hasPathNetwork);
  const supportsCoordinateConversion = hasGeoAlignment;

  return {
    hasGeoAlignment,
    hasImagePoses,
    hasPathNetwork,
    supportsAerialFlythrough,
    supportsOrbitFlythrough,
    supportsPoseWalkthrough,
    supportsRecording: scene.supportsRecording ?? false,
    supportsCoordinateConversion,
  };
}

export function inferCapabilityStates(
  scene: SceneConfig,
  hasPois: boolean
): SceneCapabilityStates {
  const hasSegmentedData = !!scene.manifest;
  const signals = inferCapabilitySignals(scene);
  const hasNavigation = signals.hasPathNetwork;
  const sceneType = scene.sceneType;

  const flythrough =
    signals.supportsAerialFlythrough
      ? {
          state: 'AVAILABLE_NOW' as const,
          mode: 'aerial' as const,
        }
      : signals.supportsOrbitFlythrough
        ? {
            state: 'AVAILABLE_NOW' as const,
            mode: 'orbit' as const,
          }
      : sceneType === 'street' && signals.supportsPoseWalkthrough
        ? {
            state: 'CAN_BE_GENERATED' as const,
            mode: 'street' as const,
            reason: 'An eye-level route can be generated from image poses or navigation data.',
          }
        : sceneType === 'indoor'
          ? {
              state: 'NOT_AVAILABLE' as const,
              mode: 'indoor' as const,
              reason: 'Indoor scenes use constrained movement instead of broad flythroughs.',
            }
          : {
              state: 'NOT_AVAILABLE' as const,
              reason: 'This scene does not describe a supported flythrough mode.',
            };

  return Object.freeze({
    measurement: {
      state: 'AVAILABLE_NOW',
    },
    navigation: hasNavigation
      ? {
          state: 'AVAILABLE_NOW',
        }
      : hasSegmentedData
        ? {
            state: 'CAN_BE_GENERATED',
            reason: 'A minimap can be generated from the segmented scene data.',
          }
        : {
            state: 'NOT_AVAILABLE',
            reason: 'This scene does not include map navigation data.',
          },
    imageView: canUseSourceImages(scene)
      ? {
          state: 'AVAILABLE_NOW',
        }
      : {
          state: 'NOT_AVAILABLE',
          reason: 'This scene does not have source images.',
        },
    pois: hasPois
      ? {
          state: 'AVAILABLE_NOW',
        }
      : {
          state: 'NOT_AVAILABLE',
          reason: 'No assets are available for this scene.',
        },
    flythrough,
    ...signals,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// defaultCapabilities
// Safe starting value before the scene finishes loading.
// ─────────────────────────────────────────────────────────────────────────────

export const defaultCapabilities: SceneCapabilities = Object.freeze({
  measurement: true as const,
  minimap:     false,
  pois:        false,
  flythrough:  false,
  imageView:   false,
  hasGeoAlignment: false,
  hasImagePoses: false,
  hasPathNetwork: false,
  supportsAerialFlythrough: false,
  supportsOrbitFlythrough: false,
  supportsPoseWalkthrough: false,
  supportsRecording: false,
  supportsCoordinateConversion: false,
});
